import { useMutation } from "@tanstack/react-query"
import axios, { AxiosError } from "axios"
import { useQueryClient } from "@tanstack/react-query"
import { displayNotify } from "../services/Notifications/NotificationServices"

interface Subtype {
    global_type_name: string,
    sub_type_name: string
}

interface Host {
    slug: string,
    is_main_host: boolean
}

interface FormValues {
    name: string,
    description: string,
    subtypes?: Subtype[],
    reg_type: string,
    is_reg_validation_required: boolean,
    tags?: string[],
    ground?: string,
    start_datetime: string,
    end_datetime: string,
    pricing?: string,
    event_hosts?: Host[],
    is_entry_reason_required: boolean,
    rent_available: boolean,
    state?: string,
    is_active: boolean,
    is_visible: boolean,
    is_payment_accepted?: boolean,
    imagesFile?: File[],
    previewFile?: File,
    sides?: any[]
    images?: {
        image_link: string,
        is_preview: boolean
    }[]
    kmzFile?: File
}

export default function useEditEvent(guID?: string) {
    const queryClient = useQueryClient()

    const uploadEventImage = async (file: File) => {
        await axios.put(`/event/${guID}/image/`, file, {
            headers: {
                'content-type': file.type,
            }
        })
    }

    const uploadEventSides = async (side: any) => {
        await axios.post(`/events/${guID}/parties/`, side)
    }

    const uploadEventPreview = async (file: File) => {
        await axios.put(`/events/${guID}/image/true/`, file, {
            headers: { 'Content-Type': file.type },
        })
    }

    const uploadEventKmz = async (file: File) => {
        await axios.put(`/events/${guID}/map/`, file, {
            headers: { 'Content-Type': file.type },
        })
    }

    return useMutation({
        mutationFn:
            async (formData: FormValues) => {
                await axios.patch(`/events/${guID}/edit/`, formData)

                const uploadErrors = []

                if (formData.imagesFile?.length) {
                    await Promise.all(
                        formData.imagesFile.map(async (file) => {
                            try {
                                uploadEventImage(file)
                            } catch {
                                uploadErrors.push('Ошибка загрузки изображений')
                            }
                        }))
                }

                if (formData.previewFile) {
                    try {
                        await uploadEventPreview(formData.previewFile)
                    } catch {
                        uploadErrors.push('Ошибка загрузки обложки')
                    }
                }

                if (formData.kmzFile) {
                    try {
                        await uploadEventKmz(formData.kmzFile)
                    } catch {
                        uploadErrors.push('Ошибка загрузки файла карты')
                    }
                }

                if (formData.sides?.length) {
                    await Promise.all(
                        formData.sides.map((side) => {
                            try {
                                uploadEventSides(side)
                            } catch {
                                uploadErrors.push('Ошибка загрузки сторон')
                            }
                        }))
                }

                return uploadErrors
            },
        onSuccess: (uploadErrors) => {
            queryClient.invalidateQueries({ queryKey: ['events', guID] })
            queryClient.invalidateQueries({ queryKey: ['eventCards'] })

            displayNotify({
                status: 'success',
                title: 'Успех!',
                description: 'Мероприятие успешно обновлено'
            })

            if (uploadErrors.length) {
                uploadErrors.forEach((e) =>
                    displayNotify({
                        status: 'error',
                        title: e,
                        description: 'Повторите попытку позже'
                    }))
            }
        },
        onError: (error) => {
            displayNotify({
                status: 'error',
                title: 'Мероприятие не обновлено',
                description: 'Проверьте корректность данных или попробуйте позже.'
            })
        }
    })
}